import { ApiError } from '../api/client';
import {
  NAME_MAX,
  STANDARD_LAYOUT_ID,
  isEmail,
  isPermissionKey,
  wellsProblem,
} from '../access/permissions';
import type {
  AdminStore,
  AdminUser,
  Company,
  CompanyStatus,
  DataSource,
  NamedLayout,
  PermissionKey,
  Role,
  WellExtra,
  WellScope,
} from '../access/permissions';
import { DEFAULT_LAYOUT, sanitiseLayout, validateLayout } from '../nav/rails';
import type { NavLayout } from '../nav/rails';
import type { Well } from '../types';

/**
 * The admin area's data — SIMULATED, for mock mode (VITE_MOCK_API=true).
 *
 * With a real API the admin area is lib/api/admin.ts talking to the server.
 * This file is what stands in for that server when the client runs on its
 * own: companies, wells created here, roles, layouts and users live in this
 * browser's localStorage. That is enough to build and try the pages, and no
 * more: nothing here reaches another computer, a user created here cannot
 * sign in, and every "only a superadmin may" is enforced by the page alone.
 *
 * Each function below stands for one call of the API described in
 * docs/api/ADMIN.md, and fails the way that call is meant to fail: 400 for
 * input that is wrong, 404 for something that is gone, 409 for a change that
 * would leave something else pointing at nothing.
 *
 * Nothing imports this file but lib/api/admin.ts.
 */

export const ADMIN_STORAGE_KEY = 'wxp.admin.v1';

/** Where the single, pre-roles menu layout used to be kept. Read once, to carry it over. */
const LEGACY_LAYOUT_KEY = 'wxp.navLayout.v1';

/**
 * The Company Admin the example data gives its company: Akira's own account,
 * which is the Company Admin of PETRONAS on the API as well.
 */
const EXAMPLE_ADMIN = { name: 'Akira', email: 'akira@groupisa.com' };

/** The made-up admin the example used to add, before it was Akira. */
const OLD_EXAMPLE_ADMIN = 'rina.hartono@example.com';

/**
 * Example data stored before the admin was Akira still names the made-up
 * person. She is replaced where she stands — same id, same company, same role
 * — unless Akira already has an account here, in which case she is dropped
 * and whoever she created counts for him.
 */
function withExampleAdmin(users: AdminUser[]): AdminUser[] {
  const old = users.find((u) => u.email === OLD_EXAMPLE_ADMIN && u.createdBy === 'example');
  if (!old) return users;

  const akira = users.some((u) => u.email === EXAMPLE_ADMIN.email);
  return users
    .filter((u) => !(akira && u.id === old.id))
    .map((u) => {
      if (u.id === old.id) return { ...u, ...EXAMPLE_ADMIN };
      return u.createdBy === OLD_EXAMPLE_ADMIN ? { ...u, createdBy: EXAMPLE_ADMIN.email } : u;
    });
}

/** Wells created here start above anything the API hands out. */
const LOCAL_WELL_FLOOR = 900_000;

const LATENCY_MS = 250;

/** Levels are not fixed at three; this only keeps a typo out. */
export const MAX_DATA_LEVEL = 99;

export const WELL_STATUSES = [
  'Drilling Operations',
  'Casing Operations',
  'Logging Operations',
  'Testing Operations',
  'Abandoning/Abandoned',
] as const;

// -------------------------------------------------------------------- store

const VIEW: PermissionKey[] = ['overview.view', 'rtlogs.view', 'documents.view', 'ai.use', 'knowledge.view'];

function seedRoles(): Role[] {
  const role = (
    id: number,
    name: string,
    description: string,
    scope: WellScope,
    permissions: PermissionKey[],
  ): Role => ({ id, name, description, scope, permissions, layoutId: STANDARD_LAYOUT_ID });

  return [
    role(
      2,
      'Company Admin',
      'Looks after the company’s people. Opens no well; creates the Well Admins and puts them on wells.',
      'none',
      ['welladmins.manage'],
    ),
    role(
      3,
      'Well Admin',
      'Runs one well: creates its users and keeps its documents in order.',
      'one',
      [...VIEW, 'documents.manage', 'users.manage'],
    ),
    role(
      4,
      'Operation Geologist',
      'Works across several wells and decides how the logs are drawn: curves, colours, properties.',
      'many',
      [...VIEW, 'documents.manage', 'logs.configure', 'data.request'],
    ),
    role(
      5,
      'Data Custody',
      'Receives requests for data, groups the data and sends it by email.',
      'many',
      ['overview.view', 'rtlogs.view', 'documents.view', 'data.fulfil'],
    ),
    role(6, 'User', 'Reads the wells they were given, and may ask Data Custody for data.', 'many', [
      'overview.view',
      'rtlogs.view',
      'documents.view',
      'ai.use',
      'data.request',
    ]),
  ];
}

function legacyLayout(): NavLayout {
  try {
    const raw = localStorage.getItem(LEGACY_LAYOUT_KEY);
    return (raw && sanitiseLayout(JSON.parse(raw))) || DEFAULT_LAYOUT;
  } catch {
    return DEFAULT_LAYOUT;
  }
}

function freshStore(): AdminStore {
  return {
    version: 1,
    seq: 100,
    companies: [],
    wells: [],
    wellExtras: {},
    layouts: [{ id: STANDARD_LAYOUT_ID, name: 'Standard', rails: legacyLayout().rails }],
    roles: seedRoles(),
    users: [],
  };
}

const list = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

/**
 * Stored data is not trusted: it may be from an older version or hand-edited.
 * Anything unusable is dropped, and the two things the portal cannot run
 * without — the standard layout and at least the seeded roles — are put back.
 */
function sanitiseStore(raw: unknown): AdminStore {
  const fresh = freshStore();
  const src = raw as Partial<AdminStore> | null;
  if (!src || src.version !== 1) return fresh;

  const layouts: NamedLayout[] = [];
  for (const l of list<Partial<NamedLayout>>(src.layouts)) {
    const clean = sanitiseLayout(l);
    if (clean && typeof l.id === 'number' && !layouts.some((x) => x.id === l.id)) {
      layouts.push({ id: l.id, name: String(l.name ?? '').slice(0, NAME_MAX) || 'Layout', rails: clean.rails });
    }
  }
  if (!layouts.some((l) => l.id === STANDARD_LAYOUT_ID)) layouts.unshift(fresh.layouts[0]);

  const roles = list<Role>(src.roles)
    .filter((r) => r && typeof r.id === 'number' && typeof r.name === 'string')
    .map((r) => ({
      ...r,
      description: String(r.description ?? ''),
      scope: (['none', 'one', 'many', 'all'] as const).includes(r.scope) ? r.scope : 'many',
      permissions: list<unknown>(r.permissions).filter(isPermissionKey),
      layoutId: layouts.some((l) => l.id === r.layoutId) ? r.layoutId : STANDARD_LAYOUT_ID,
    }));

  const extras: Record<string, WellExtra> = {};
  for (const [id, extra] of Object.entries(src.wellExtras ?? {})) {
    extras[id] = {
      companyId: typeof extra?.companyId === 'number' ? extra.companyId : null,
      sources: list<DataSource>(extra?.sources).filter((s) => s && typeof s.id === 'number'),
    };
  }

  return {
    version: 1,
    seq: typeof src.seq === 'number' ? src.seq : fresh.seq,
    companies: list<Company>(src.companies).filter((c) => c && typeof c.id === 'number'),
    wells: list<Well>(src.wells).filter((w) => w && typeof w.id === 'number'),
    wellExtras: extras,
    layouts,
    roles,
    users: withExampleAdmin(
      list<AdminUser>(src.users)
        .filter((u) => u && typeof u.id === 'number' && typeof u.email === 'string')
        .map((u) => ({ ...u, wellIds: list<number>(u.wellIds) })),
    ),
  };
}

/** Synchronous on purpose: the first paint already has the right menu. */
export function readAdminStore(): AdminStore {
  try {
    const raw = localStorage.getItem(ADMIN_STORAGE_KEY);
    return raw ? sanitiseStore(JSON.parse(raw)) : freshStore();
  } catch {
    return freshStore();
  }
}

function write(store: AdminStore): AdminStore {
  try {
    localStorage.setItem(ADMIN_STORAGE_KEY, JSON.stringify(store));
  } catch {
    throw new ApiError('This browser would not store the change.', 0);
  }
  return store;
}

function pause() {
  return new Promise<void>((resolve) => window.setTimeout(resolve, LATENCY_MS));
}

/** One simulated request: read what is stored now, change it, write it back. */
async function mutate(change: (store: AdminStore) => void): Promise<AdminStore> {
  await pause();
  const store = readAdminStore();
  change(store);
  return write(store);
}

const invalid = (message: string) => new ApiError(message, 400);
const conflict = (message: string) => new ApiError(message, 409);
const missing = (what: string) => new ApiError(`That ${what} no longer exists.`, 404);

function cleanName(value: string, what: string): string {
  const name = value.trim().replace(/\s+/g, ' ');
  if (!name) throw invalid(`The ${what} needs a name.`);
  if (name.length > NAME_MAX) throw invalid(`The ${what} name is too long (${NAME_MAX} characters at most).`);
  return name;
}

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// ---------------------------------------------------------------- companies

export interface CompanyInput {
  name: string;
  code: string;
  active: boolean;
  legalName?: string;
  /** Left out, the status follows `active`. */
  status?: CompanyStatus;
  /**
   * The wells filed under the company, all of them. Left out, the wells are
   * not touched. Only a well that belongs to no company can be taken in, and
   * a well with people on it cannot be let go.
   */
  wellIds?: number[];
}

/** Files exactly `wellIds` under the company; see CompanyInput.wellIds. */
function fileWells(store: AdminStore, companyId: number, companyName: string, wellIds: number[]) {
  const want = new Set(wellIds);
  const local = new Set(store.wells.map((w) => w.id));

  for (const [key, extra] of Object.entries(store.wellExtras)) {
    const wellId = Number(key);
    if (extra.companyId !== companyId || want.has(wellId)) continue;

    const assigned = store.users.filter((u) => u.wellIds.includes(wellId)).length;
    if (assigned > 0) {
      throw conflict(
        `${count(assigned, 'user')} of ${companyName} ${assigned === 1 ? 'is' : 'are'} assigned to a well you removed. Take them off it first.`,
      );
    }
    if (local.has(wellId)) {
      throw conflict('A well created in the admin area belongs to its company. Delete it on the Wells page instead.');
    }
    extra.companyId = null;
  }

  for (const wellId of want) {
    const extra = store.wellExtras[String(wellId)];
    const at = extra?.companyId ?? null;
    if (at === companyId) continue;
    const owner = store.companies.find((c) => c.id === at);
    if (owner) throw conflict(`One of those wells already belongs to ${owner.name}.`);
    store.wellExtras[String(wellId)] = { companyId, sources: extra?.sources ?? [] };
  }
}

/** POST /api/companies (id absent) · PUT /api/companies/{id} */
export function saveCompany(id: number | null, input: CompanyInput): Promise<AdminStore> {
  return mutate((store) => {
    const name = cleanName(input.name, 'company');
    const code = input.code.trim().toUpperCase();
    if (!/^[A-Z0-9]{2,8}$/.test(code)) throw invalid('The code is 2 to 8 letters or digits, for example EXE.');

    for (const other of store.companies) {
      if (other.id === id) continue;
      if (sameName(other.name, name)) throw conflict(`There is already a company called “${other.name}”.`);
      if (other.code === code) throw conflict(`The code ${code} belongs to ${other.name}.`);
    }

    const status: CompanyStatus = input.status ?? (input.active ? 'active' : 'inactive');
    const fields = { name, code, status, active: status === 'active' };
    const legalName = input.legalName?.trim().replace(/\s+/g, ' ').slice(0, NAME_MAX);

    if (id == null) {
      store.seq += 1;
      const companyId = store.seq;
      store.companies.push({ id: companyId, ...fields, legalName, createdAt: new Date().toISOString() });
      if (input.wellIds) fileWells(store, companyId, name, input.wellIds);
      return;
    }

    const have = store.companies.find((c) => c.id === id);
    if (!have) throw missing('company');
    Object.assign(have, fields);
    // A caller that does not know about legal names leaves the one there is.
    if (input.legalName !== undefined) have.legalName = legalName;
    if (input.wellIds) fileWells(store, id, name, input.wellIds);
  });
}

/** DELETE /api/companies/{id} — refused while anything still belongs to it. */
export function deleteCompany(id: number): Promise<AdminStore> {
  return mutate((store) => {
    const have = store.companies.find((c) => c.id === id);
    if (!have) throw missing('company');

    const users = store.users.filter((u) => u.companyId === id).length;
    const wells = Object.values(store.wellExtras).filter((e) => e.companyId === id).length;
    if (users > 0 || wells > 0) {
      throw conflict(
        `${have.name} still has ${count(wells, 'well')} and ${count(users, 'user')}. Move or remove them first, or switch the company off instead.`,
      );
    }
    store.companies = store.companies.filter((c) => c.id !== id);
  });
}

// -------------------------------------------------------------------- wells

export interface WellInput {
  name: string;
  field: string;
  country: string;
  rig: string;
  purpose: string;
  status: string;
  latitude: number | null;
  longitude: number | null;
  companyId: number | null;
  sources: { id?: number; company: string; level: number; realtime: boolean }[];
}

function cleanSources(store: AdminStore, input: WellInput['sources']): DataSource[] {
  const names = new Set<string>();
  return input.map((s, index) => {
    const company = s.company.trim();
    if (!company) throw invalid(`Data source ${index + 1} needs the service company’s name.`);
    if (names.has(company.toLowerCase())) throw invalid(`“${company}” is listed twice as a data source.`);
    names.add(company.toLowerCase());
    if (!Number.isInteger(s.level) || s.level < 1 || s.level > MAX_DATA_LEVEL) {
      throw invalid(`A data level is a whole number from 1 to ${MAX_DATA_LEVEL}.`);
    }

    let id = s.id;
    if (id == null) {
      store.seq += 1;
      id = store.seq;
    }
    return { id, company, level: s.level, realtime: s.realtime };
  });
}

/**
 * POST /api/wells (id absent) · PUT /api/wells/{id}
 *
 * A well the API already serves keeps the fields the API gave it; only its
 * company and data sources are held here. `existing` says which kind it is.
 */
export function saveWell(
  id: number | null,
  input: WellInput,
  existing: { local: boolean; names: string[] },
): Promise<AdminStore> {
  return mutate((store) => {
    if (input.companyId != null && !store.companies.some((c) => c.id === input.companyId)) {
      throw invalid('Pick a company that exists.');
    }
    const extra: WellExtra = { companyId: input.companyId, sources: cleanSources(store, input.sources) };

    // The company of a well cannot change under the people working on it.
    if (id != null) {
      const before = store.wellExtras[String(id)]?.companyId ?? null;
      const assigned = store.users.filter((u) => u.wellIds.includes(id)).length;
      if (before !== input.companyId && assigned > 0) {
        throw conflict(
          `${count(assigned, 'user')} of the current company ${assigned === 1 ? 'is' : 'are'} assigned to this well. Take them off it before moving it to another company.`,
        );
      }
    }

    if (id != null && !existing.local) {
      store.wellExtras[String(id)] = extra;
      return;
    }

    const name = cleanName(input.name, 'well');
    if (existing.names.some((other) => sameName(other, name))) {
      throw conflict(`There is already a well called “${name}”.`);
    }
    if (input.companyId == null) throw invalid('A new well belongs to a company. Pick one.');

    const { latitude, longitude } = input;
    if (latitude != null && !(latitude >= -90 && latitude <= 90)) throw invalid('Latitude is a number between -90 and 90.');
    if (longitude != null && !(longitude >= -180 && longitude <= 180)) throw invalid('Longitude is a number between -180 and 180.');

    const fields = {
      name,
      field: input.field.trim(),
      country: input.country.trim(),
      rig: input.rig.trim(),
      purpose: input.purpose.trim(),
      status: input.status,
      latitude: latitude ?? undefined,
      longitude: longitude ?? undefined,
    };

    if (id == null) {
      store.seq += 1;
      const wellId = LOCAL_WELL_FLOOR + store.seq;
      store.wells.push({ id: wellId, ...fields, createdAt: new Date().toISOString() });
      store.wellExtras[String(wellId)] = extra;
      return;
    }

    const have = store.wells.find((w) => w.id === id);
    if (!have) throw missing('well');
    Object.assign(have, fields);
    store.wellExtras[String(id)] = extra;
  });
}

/** DELETE /api/wells/{id} — only a well created here, and only once nobody is on it. */
export function deleteWell(id: number): Promise<AdminStore> {
  return mutate((store) => {
    const have = store.wells.find((w) => w.id === id);
    if (!have) throw missing('well');

    const assigned = store.users.filter((u) => u.wellIds.includes(id)).length;
    if (assigned > 0) {
      throw conflict(`${count(assigned, 'user')} still ${assigned === 1 ? 'is' : 'are'} assigned to ${have.name}.`);
    }
    store.wells = store.wells.filter((w) => w.id !== id);
    delete store.wellExtras[String(id)];
  });
}

// ------------------------------------------------------------------ layouts

/** POST /api/layouts — a new layout starts as a copy of another. */
export function createLayout(name: string, copyOf: number): Promise<AdminStore> {
  return mutate((store) => {
    const clean = cleanName(name, 'layout');
    if (store.layouts.some((l) => sameName(l.name, clean))) {
      throw conflict(`There is already a layout called “${clean}”.`);
    }
    const source = store.layouts.find((l) => l.id === copyOf);
    if (!source) throw missing('layout');

    store.seq += 1;
    store.layouts.push({ id: store.seq, name: clean, rails: structuredClone(source.rails) });
  });
}

/** PUT /api/layouts/{id} */
export function saveLayout(id: number, name: string, layout: NavLayout): Promise<AdminStore> {
  return mutate((store) => {
    const have = store.layouts.find((l) => l.id === id);
    if (!have) throw missing('layout');

    const cleanedName = cleanName(name, 'layout');
    if (store.layouts.some((l) => l.id !== id && sameName(l.name, cleanedName))) {
      throw conflict(`There is already a layout called “${cleanedName}”.`);
    }

    const clean = sanitiseLayout(layout);
    if (!clean) throw invalid('That layout is not valid.');
    const problems = validateLayout(clean);
    if (problems.length > 0) throw invalid(problems[0]);

    have.name = cleanedName;
    have.rails = clean.rails;
  });
}

/** DELETE /api/layouts/{id} — never the standard one, never one a role uses. */
export function deleteLayout(id: number): Promise<AdminStore> {
  return mutate((store) => {
    const have = store.layouts.find((l) => l.id === id);
    if (!have) throw missing('layout');
    if (id === STANDARD_LAYOUT_ID) throw conflict('The standard layout cannot be deleted.');

    const users = store.roles.filter((r) => r.layoutId === id).map((r) => r.name);
    if (users.length > 0) {
      throw conflict(`${have.name} is the layout of ${users.join(', ')}. Give ${users.length === 1 ? 'that role' : 'those roles'} another layout first.`);
    }
    store.layouts = store.layouts.filter((l) => l.id !== id);
  });
}

// -------------------------------------------------------------------- roles

export interface RoleInput {
  name: string;
  description: string;
  scope: WellScope;
  permissions: PermissionKey[];
  layoutId: number;
}

/** POST /api/roles (id absent) · PUT /api/roles/{id} */
export function saveRole(id: number | null, input: RoleInput): Promise<AdminStore> {
  return mutate((store) => {
    const name = cleanName(input.name, 'role');
    if (sameName(name, 'superadmin')) throw conflict('Superadmin is built in and cannot be redefined.');
    if (store.roles.some((r) => r.id !== id && sameName(r.name, name))) {
      throw conflict(`There is already a role called “${name}”.`);
    }
    if (!store.layouts.some((l) => l.id === input.layoutId)) throw invalid('Pick a layout that exists.');

    const permissions = [...new Set(input.permissions.filter(isPermissionKey))];

    // Someone who opens no well has nothing to read on one.
    if (input.scope === 'none' && permissions.some((p) => !p.endsWith('.manage') || p === 'documents.manage')) {
      throw invalid('A role with no wells can only hold the administration permissions.');
    }

    const fields = { name, description: input.description.trim(), scope: input.scope, permissions, layoutId: input.layoutId };

    if (id == null) {
      store.seq += 1;
      store.roles.push({ id: store.seq, ...fields });
      return;
    }

    const have = store.roles.find((r) => r.id === id);
    if (!have) throw missing('role');

    // Narrowing the scope must not leave people holding wells the role no longer allows.
    const holders = store.users.filter((u) => u.roleId === id);
    const broken = holders.filter((u) => wellsProblem({ ...have, ...fields }, u.wellIds) !== null);
    if (broken.length > 0 && input.scope !== have.scope) {
      throw conflict(
        `${count(broken.length, 'user')} with this role ${broken.length === 1 ? 'does' : 'do'} not fit the new well access (${broken
          .slice(0, 3)
          .map((u) => u.name)
          .join(', ')}${broken.length > 3 ? '…' : ''}). Change their wells first.`,
      );
    }
    Object.assign(have, fields);
  });
}

/** DELETE /api/roles/{id} — refused while somebody holds it. */
export function deleteRole(id: number): Promise<AdminStore> {
  return mutate((store) => {
    const have = store.roles.find((r) => r.id === id);
    if (!have) throw missing('role');

    const holders = store.users.filter((u) => u.roleId === id).length;
    if (holders > 0) throw conflict(`${count(holders, 'user')} still ${holders === 1 ? 'has' : 'have'} the role ${have.name}.`);
    store.roles = store.roles.filter((r) => r.id !== id);
  });
}

// -------------------------------------------------------------------- users

export interface UserInput {
  name: string;
  email: string;
  companyId: number | null;
  roleId: number | null;
  wellIds: number[];
  active: boolean;
}

/**
 * POST /api/users (id absent) · PUT /api/users/{id}
 *
 * `companyWellIds` is the wells of the chosen company as the caller sees them
 * — the API's and the ones created here — since this file only knows the
 * latter. Whether the caller may create this role at all is the page's check
 * here and the server's in the real thing.
 */
export function saveUser(
  id: number | null,
  input: UserInput,
  context: { actor: string; companyWellIds: number[] },
): Promise<AdminStore> {
  return mutate((store) => {
    const name = cleanName(input.name, 'user');
    const email = input.email.trim().toLowerCase();
    if (!isEmail(email)) throw invalid('That is not an email address.');
    if (store.users.some((u) => u.id !== id && u.email === email)) {
      throw conflict(`${email} already has an account.`);
    }

    const company = store.companies.find((c) => c.id === input.companyId);
    if (!company) throw invalid('Pick the company this person belongs to.');
    const role = store.roles.find((r) => r.id === input.roleId);
    if (!role) throw invalid('Pick a role.');

    const pickable = role.scope === 'one' || role.scope === 'many';
    const wellIds = pickable ? [...new Set(input.wellIds)].sort((a, b) => a - b) : [];
    if (wellIds.some((w) => !context.companyWellIds.includes(w))) {
      throw invalid(`A user of ${company.name} can only be put on ${company.name}’s wells.`);
    }
    const problem = wellsProblem(role, wellIds);
    if (problem) throw invalid(problem);

    const fields = { name, companyId: company.id, roleId: role.id, wellIds, active: input.active };

    if (id == null) {
      store.seq += 1;
      store.users.push({
        id: store.seq,
        email,
        ...fields,
        createdAt: new Date().toISOString(),
        createdBy: context.actor,
      });
      return;
    }

    const have = store.users.find((u) => u.id === id);
    if (!have) throw missing('user');
    // The email is how the person signs in; it is fixed once the account exists.
    if (have.email !== email) throw invalid('The email of an existing account cannot be changed.');
    Object.assign(have, fields);
  });
}

/** DELETE /api/users/{id} */
export function deleteUser(id: number): Promise<AdminStore> {
  return mutate((store) => {
    if (!store.users.some((u) => u.id === id)) throw missing('user');
    store.users = store.users.filter((u) => u.id !== id);
  });
}

// ------------------------------------------------------- example and reset

/**
 * Fills an empty admin area with one company, its people and data sources, so
 * the pages can be looked at without typing it all in. Only offered while
 * there are no companies. `wellIds` are the wells the API is serving.
 */
export function loadExample(companyName: string, wellIds: number[]): Promise<AdminStore> {
  return mutate((store) => {
    if (store.companies.length > 0) throw conflict('The example is only for an empty admin area.');

    const next = () => (store.seq += 1);
    const now = new Date().toISOString();

    const companyId = next();
    const code = companyName.replace(/[^a-z0-9]/gi, '').slice(0, 3).toUpperCase() || 'EXA';
    store.companies.push({ id: companyId, name: companyName, code, active: true, createdAt: now });

    wellIds.forEach((id, index) => {
      store.wellExtras[String(id)] = {
        companyId,
        sources:
          index === 0
            ? [
                { id: next(), company: 'Wireline contractor', level: 3, realtime: false },
                { id: next(), company: 'LWD contractor', level: 2, realtime: false },
                { id: next(), company: 'Mudlogging contractor', level: 1, realtime: true },
              ]
            : [],
      };
    });

    const roleId = (name: string) => store.roles.find((r) => r.name === name)?.id;
    const person = (name: string, email: string, role: string, wells: number[]) => {
      const id = roleId(role);
      if (id == null) return;
      store.users.push({
        id: next(),
        name,
        email,
        companyId,
        roleId: id,
        wellIds: wells,
        active: true,
        createdAt: now,
        createdBy: 'example',
      });
    };

    // The company's one Company Admin is a real account, not a made-up person:
    // the same one the API files under this company (migrations/0015).
    person(EXAMPLE_ADMIN.name, EXAMPLE_ADMIN.email, 'Company Admin', []);
    if (wellIds.length > 0) {
      person('Budi Santoso', 'budi.santoso@example.com', 'Well Admin', wellIds.slice(0, 1));
      person('Sari Wulandari', 'sari.wulandari@example.com', 'Operation Geologist', wellIds.slice(0, 2));
      person('Dimas Pratama', 'dimas.pratama@example.com', 'Data Custody', wellIds.slice(0, 2));
      person('Maya Putri', 'maya.putri@example.com', 'User', wellIds.slice(0, 1));
    }
  });
}

/** Throws away everything stored here, layouts included. */
export async function resetAdminStore(): Promise<AdminStore> {
  await pause();
  try {
    localStorage.removeItem(ADMIN_STORAGE_KEY);
    localStorage.removeItem(LEGACY_LAYOUT_KEY);
  } catch {
    /* nothing stored, nothing to remove */
  }
  return freshStore();
}
